import React, { useState, useEffect } from "react";
import type { Assignment } from "../../types/shared";
import PAStatusTimeline from "../PAStatusTimeline";
import InsurerLetterUpload from "./InsurerLetterUpload";

interface StatusConfig {
  label: string;
  bg: string;
  color: string;
  icon: string;
}

/** Form assignment row enriched with its PA timeline + display status. */
interface EnrichedForm extends Assignment {
  title?: string;
  pa?: any;
  displayStatus: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  draft: { label: "Draft", bg: "#f3f4f6", color: "#999", icon: "📝" },
  pending_patient: { label: "Pending Your Signature", bg: "#fdf4ea", color: "#c9772a", icon: "✍️" },
  pending_plan_member: { label: "Awaiting Plan Member Signature", bg: "#fdf4ea", color: "#c9772a", icon: "✍️" },
  pending_provider: { label: "Awaiting Provider Review", bg: "#e4ecee", color: "#154b58", icon: "👨‍⚕️" },
  physician_completed: { label: "Physician Signed", bg: "#eef4f2", color: "#6b9c8e", icon: "✅" },
  submitted_to_insurer: { label: "Sent to Insurance", bg: "#ede7f6", color: "#734a76", icon: "📤" },
  under_review: { label: "Under Review", bg: "#fdf4ea", color: "#e65100", icon: "🔍" },
  requires_info: { label: "Info Requested", bg: "#fdf4ea", color: "#c9772a", icon: "📋" },
  approved: { label: "Insurance Approved", bg: "#eef4f2", color: "#38534a", icon: "🎉" },
  denied: { label: "Insurance Declined", bg: "#faeeed", color: "#c25450", icon: "❌" },
  // Not a decline: the plan does not include this medication (case imp_851c22ae).
  plan_exclusion: { label: "Not Included in Your Plan", bg: "#f3f4f6", color: "#5b6e7c", icon: "ℹ️" },
  pharmacy_notified: { label: "Pharmacy Notified", bg: "#eef4f2", color: "#6b9c8e", icon: "💊" },
  ready_for_pickup: { label: "Ready for Pickup", bg: "#eef4f2", color: "#38534a", icon: "📦" },
  completed: { label: "Dispensed", bg: "#eef4f2", color: "#38534a", icon: "✅" },
  renewal_reminder: { label: "Renewal Due", bg: "#fdf4ea", color: "#c9772a", icon: "🔄" },
};

function getStatusConfig(status?: string): StatusConfig {
  return (status && STATUS_CONFIG[status]) || { label: status || "Unknown", bg: "#f3f4f6", color: "#666", icon: "📄" };
}

interface FormTrackerSectionProps {
  patientId?: string;
}

export default function FormTrackerSection({ patientId }: FormTrackerSectionProps) {
  const [forms, setForms] = useState<Assignment[]>([]);
  const [paTimelines, setPaTimelines] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedPa, setExpandedPa] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) { setLoading(false); return; }

    Promise.all([
      fetch(`/api/patient/${encodeURIComponent(patientId)}/forms`)
        .then(r => r.ok ? r.json() : { forms: [] })
        .then(d => d.forms || []),
      fetch(`/api/tracking/patient/${encodeURIComponent(patientId)}`)
        .then(r => r.ok ? r.json() : { pa_submissions: [] })
        .then(d => d.pa_submissions || []),
    ])
      .then(([f, pa]) => {
        setForms(f);
        setPaTimelines(pa);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [patientId]);

  if (!patientId) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#5b6e7c" }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📄</div>
        <p style={{ fontSize: 16 }}>Set up your profile to track your forms</p>
      </div>
    );
  }

  // Build a map of PA timeline by assignment_id for enrichment
  const paMap: Record<string, any> = {};
  paTimelines.forEach(pa => { paMap[pa.assignment_id] = pa; });

  // Merge forms with PA data
  const enrichedForms: EnrichedForm[] = forms.map(f => {
    const pa = paMap[f.assignment_id];
    const currentStage = pa?.current_stage;
    // Use PA current stage if more advanced than form status
    const displayStatus = currentStage || f.status || "draft";
    return { ...f, pa, displayStatus };
  });

  // Filter
  const filtered = filter === "all"
    ? enrichedForms
    : filter === "action"
      ? enrichedForms.filter(f => f.displayStatus === "pending_patient" || f.displayStatus === "requires_info")
      : filter === "active"
        ? enrichedForms.filter(f => !["completed", "denied", "plan_exclusion", "draft"].includes(f.displayStatus))
        : enrichedForms.filter(f => f.displayStatus === filter);

  const actionCount = enrichedForms.filter(f => f.displayStatus === "pending_patient" || f.displayStatus === "requires_info").length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#182a33", margin: 0 }}>Form Tracker</h2>
          <p style={{ fontSize: 13, color: "#5b6e7c", margin: "4px 0 0" }}>
            Track your forms from assignment through to prescription pickup
          </p>
        </div>
        {actionCount > 0 && (
          <span style={{
            padding: "6px 14px", borderRadius: 16, fontSize: 13, fontWeight: 600,
            backgroundColor: "#fdf4ea", color: "#c9772a",
          }}>
            {actionCount} action{actionCount > 1 ? "s" : ""} needed
          </span>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { id: "all", label: "All" },
          { id: "action", label: "Action Needed" },
          { id: "active", label: "In Progress" },
          { id: "completed", label: "Completed" },
          { id: "denied", label: "Declined" },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            style={{
              padding: "6px 14px", fontSize: 13, fontWeight: filter === tab.id ? 600 : 400,
              backgroundColor: filter === tab.id ? "#182a33" : "#fff",
              color: filter === tab.id ? "#fff" : "#182a33",
              border: `1px solid ${filter === tab.id ? "#182a33" : "#d8c9b6"}`,
              borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#5b6e7c" }}>Loading forms...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          backgroundColor: "white", borderRadius: 12, padding: "48px 28px",
          border: "1px solid #e6dccb", textAlign: "center",
        }}>
          <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>📄</div>
          <p style={{ fontSize: 15, color: "#5b6e7c" }}>
            {filter === "all" ? "No forms assigned yet" : "No forms match this filter"}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((form, i) => {
            const sc = getStatusConfig(form.displayStatus);
            const isPending = form.displayStatus === "pending_patient";
            const hasPa = !!form.pa;
            // Once the patient has signed (anything past draft / pending-signature),
            // offer their completed copy — the filled, signed PDF lives server-side
            // and is rendered on demand. This is the patient's in-portal copy.
            const hasSigned = !["draft", "pending_patient"].includes(form.displayStatus);
            const isExpanded = expandedPa === form.assignment_id;
            // Show the insurer-letter upload affordance for any form that has been
            // submitted (past draft/pending-patient). Insurers may have sent a
            // determination letter by mail or patient portal regardless of PA stage.
            const showLetterUpload = hasSigned && form.assignment_id;
            const displayDate = form.assigned_date
              ? new Date(form.assigned_date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
              : "";

            return (
              <div key={form.assignment_id || i} style={{
                backgroundColor: "white",
                borderRadius: 12,
                border: isPending ? "2px solid #f0c040" : "1px solid #e6dccb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}>
                {/* Form row */}
                <div style={{
                  padding: "16px 20px",
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 16,
                  alignItems: "center",
                }}>
                  {/* Form info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#182a33", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {form.form_title || form.title || form.form_id}
                    </div>
                    <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                      {displayDate && (
                        <span style={{ fontSize: 12, color: "#5b6e7c" }}>
                          Assigned {displayDate}
                        </span>
                      )}
                      {form.assigned_by && (
                        <span style={{ fontSize: 12, color: "#5b6e7c" }}>
                          Provider: {form.assigned_by}
                        </span>
                      )}
                      {form.pa?.insurer_name && (
                        <span style={{ fontSize: 12, color: "#5b6e7c" }}>
                          Insurer: {form.pa.insurer_name}
                        </span>
                      )}
                    </div>

                    {/* Denial reason */}
                    {form.displayStatus === "denied" && form.pa?.denial_reason && (
                      <div style={{
                        marginTop: 6, padding: "6px 10px",
                        backgroundColor: "#faeeed", borderRadius: 6,
                        fontSize: 12, color: "#c25450",
                      }}>
                        Reason: {form.pa.denial_reason}
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div style={{
                    padding: "5px 14px", borderRadius: 16, fontSize: 12, fontWeight: 600,
                    backgroundColor: sc.bg, color: sc.color, whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span>{sc.icon}</span>
                    {sc.label}
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isPending && (
                      <a
                        href={`/form/${form.form_id}?assignment=${form.assignment_id}`}
                        style={{
                          padding: "6px 16px", fontSize: 13, fontWeight: 600,
                          backgroundColor: "#182a33", color: "#fff",
                          borderRadius: 8, textDecoration: "none",
                        }}
                      >
                        Sign
                      </a>
                    )}
                    {hasSigned && (
                      <a
                        href={`/api/patient/form/${encodeURIComponent(form.assignment_id)}/completed-pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: "6px 12px", fontSize: 12, fontWeight: 600,
                          backgroundColor: "transparent", color: "#154b58",
                          border: "1px solid #b8cdd2", borderRadius: 6,
                          textDecoration: "none", whiteSpace: "nowrap",
                        }}
                      >
                        View copy
                      </a>
                    )}
                    {hasPa && (
                      <button
                        onClick={() => setExpandedPa(isExpanded ? null : form.assignment_id)}
                        style={{
                          padding: "6px 12px", fontSize: 12, fontWeight: 500,
                          backgroundColor: "transparent", color: "#182a33",
                          border: "1px solid #d8c9b6", borderRadius: 6, cursor: "pointer",
                        }}
                      >
                        {isExpanded ? "Hide" : "Track"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded PA timeline */}
                {isExpanded && form.pa && (
                  <div style={{ padding: "0 20px 16px", borderTop: "1px solid #f5f0eb" }}>
                    <PAStatusTimeline timeline={form.pa} expanded />
                  </div>
                )}

                {/* Insurer letter upload — G1.5 */}
                {showLetterUpload && (
                  <div style={{ padding: "0 20px 16px", borderTop: "1px solid #f5f0eb" }}>
                    <InsurerLetterUpload assignmentId={form.assignment_id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
